import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  RequireAuth,
  TreeViewPageShell,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import usePosthogSessionInfo from '@elementary/common-ui/hooks/tracking/usePosthogSessionInfo';
import { useAuth } from '@frontegg/react';
import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import NavigateWithEnv from 'shared/components/NavigateWithEnv';
import ReportEmptyStatePage from 'shared/components/ReportEmptyStatePage';
import { axiosInterceptors } from 'shared/utils/axiosInterceptors';
import { TestOverviewFullscreen } from 'shared/components/TestOverviewSidePanel';
import useEnvironments from 'shared/hooks/useEnvironments';
import useSupportChat from 'shared/hooks/useSupportChat';
import AlertRules from 'alertRules';
import Catalog from 'catalog';
import Dashboard from 'dashboard';
import DataHealth from 'dataHealth';
import Incidents from 'incidents';
import Lineage from 'lineage';
import MetricsExplorer from 'metricsExplorer';
import ModelRuns from 'modelRuns';
import PullRequests from 'pullRequests';
import SettingsRoutes from 'settings/components/Routes';
import TestManagement from 'testManagement';
import TestResults from 'testResults';
import TestRuns from 'testRuns';
import useFlags from 'shared/hooks/useFlags';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import { AiChatConversationPage } from 'aiChat';
import AppShell from './AppShell';
import RequireEnv from './RequireEnv';
import '@elementary/common-ui/utils/datetime';
import ErrorPage from './ErrorPage';

const App = () => {
  const { user } = useAuth();
  const { data: account } = useAccount();
  const supportChat = useSupportChat();
  const navigate = useNavigate();
  const { hasFlag } = useFlags();
  const hasEnvPermission = useEnvPermission();
  const hasDbtProjects = useHasDbtProjects();
  const { sessionId: posthogSessionId, sessionUrl: posthogSessionUrl } =
    usePosthogSessionInfo();

  const [shouldRedirectToEnvPage, setShouldRedirectToEnvPage] = useState(true);

  const defaultRedirect =
    hasEnvPermission('VIEW_ASSETS') && hasEnvPermission('VIEW_TESTS')
      ? '/report/dashboard'
      : '/catalog';

  const { selectedEnvId } = useEnvironments({
    onEnvironmentsLoaded: (data) => {
      if (!data?.[0] && shouldRedirectToEnvPage) {
        navigate('settings/environments', { replace: true });
      }
      setShouldRedirectToEnvPage(false);
    },
  });

  useEffect(() => {
    const isImpersonated = !!user?.act;

    Sentry.setTag('accountId', account?.id);
    Sentry.setTag('accountName', account?.name);
    Sentry.setTag('userId', user?.id);
    Sentry.setTag('userName', user?.name);
    Sentry.setTag('userEmail', user?.email);
    Sentry.setTag('envId', selectedEnvId);
    Sentry.setTag('posthogSessionId', posthogSessionId);
    Sentry.setTag('isImpersonated', isImpersonated);

    datadogRum.setUser({
      // NOTE: id, name and email are reserved fields, unlike the rest which are custom.
      // Generated metrics based on RUM data in DD can only be grouped by these three, so we are hacking the name and email fields to contain
      // the account name and env id, which we'd like to be able to group on.
      id: user?.id,
      name: account?.name,
      email: selectedEnvId,

      // Custom fields
      accountId: account?.id,
      accountName: account?.name,
      userName: user?.name,
      userEmail: user?.email,
      envId: selectedEnvId,
      posthogSessionId,
      posthogSessionUrl,
      isImpersonated,
    });
  }, [user, account, selectedEnvId, posthogSessionId, posthogSessionUrl]);

  useEffect(() => {
    if (user && account) {
      supportChat.init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, account]);

  return (
    <Routes>
      <Route
        element={
          <RequireAuth onAuthenticated={axiosInterceptors}>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/:envid/*" element={<RequireEnv />}>
          <Route
            element={
              <TreeViewPageShell
                renderEmptyStatePage={() => <ReportEmptyStatePage />}
                renderErrorPage={({ error }) => <ErrorPage error={error} />}
                useApi
              />
            }
          >
            {hasEnvPermission('VIEW_ASSETS') &&
              hasEnvPermission('VIEW_TESTS') && (
                <Route path="report/dashboard" element={<Dashboard />} />
              )}
            {hasEnvPermission('VIEW_TESTS') && (
              <Route
                path="report/test-results/:id?"
                element={<TestResults />}
              />
            )}
            {hasEnvPermission('VIEW_TESTS') && (
              <Route path="report/test-runs/:id?" element={<TestRuns />} />
            )}
            {hasEnvPermission('VIEW_ASSETS') && (
              <Route path="report/model-runs/:id?" element={<ModelRuns />} />
            )}
            {hasEnvPermission('VIEW_ASSETS') && (
              <Route path="report/lineage" element={<Lineage />} />
            )}
            {hasEnvPermission('VIEW_ASSETS') &&
              hasEnvPermission('VIEW_TESTS') && (
                <Route path="data-health" element={<DataHealth />} />
              )}
            {hasEnvPermission('VIEW_ASSETS') && (
              <Route path="catalog/*" element={<Catalog />} />
            )}
            {hasEnvPermission('VIEW_METRICS') && (
              <Route path="metrics" element={<MetricsExplorer />} />
            )}
            {hasFlag('test_management') &&
              hasEnvPermission('EDIT_TEST_CONFIGURATIONS') && (
                <Route path="test-management/*" element={<TestManagement />} />
              )}

            {hasEnvPermission('VIEW_INCIDENTS') && (
              <Route path="incidents/*" element={<Incidents />} />
            )}
          </Route>
          {hasDbtProjects &&
            (hasEnvPermission('EDIT_TESTS') ||
              hasEnvPermission('EDIT_ASSETS')) && (
              <Route path="pull-requests/*" element={<PullRequests />} />
            )}
          {(hasEnvPermission('VIEW_ALERT_RULES') ||
            hasEnvPermission('VIEW_DIGEST_RULES')) && (
            <Route path="alert-rules/*" element={<AlertRules />} />
          )}

          {hasEnvPermission('VIEW_TESTS') && (
            <Route path="test-triage/*" element={<TestOverviewFullscreen />} />
          )}

          <Route
            path="*"
            element={<NavigateWithEnv to={defaultRedirect} replace />}
          />
        </Route>

        <Route path="settings/*" element={<SettingsRoutes />} />

        <Route path="ai-chat/:threadId?" element={<AiChatConversationPage />} />

        <Route
          path="*"
          element={<NavigateWithEnv to={defaultRedirect} replace />}
        />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default App;
